import React from 'react';
import { Heart, ArrowLeft, Cookie } from 'lucide-react';
import Header from './Header';

function CookiePolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
    <Header/>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="flex items-center mb-6">
            <Cookie className="w-8 h-8 text-amber-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Cookie Policy</h1>
          </div>
          <p className="text-gray-600 mb-8">Last updated: June 11, 2025</p>

          <div className="prose max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">What Are Cookies?</h2>
              <p className="text-gray-700 mb-4">
                Cookies are small text files that are stored on your device when you visit our website. They help us provide you with a better, safer, and more personalized experience while maintaining your privacy and anonymity.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">How We Use Cookies</h2>
              <p className="text-gray-700 mb-4">
                VineMatched uses cookies responsibly to enhance your experience while protecting your privacy:
              </p>

              <div className="space-y-6">
                <div className="border-l-4 border-green-400 pl-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Essential Cookies (Required)</h3>
                  <p className="text-gray-700 mb-2">These cookies are necessary for the platform to function:</p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-1">
                    <li>Session management and anonymous user identification</li>
                    <li>Security and authentication</li>
                    <li>Chat connection stability</li>
                    <li>Platform functionality and navigation</li>
                  </ul>
                </div>

                <div className="border-l-4 border-blue-400 pl-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Functional Cookies (Optional)</h3>
                  <p className="text-gray-700 mb-2">These cookies improve your experience:</p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-1">
                    <li>Language and accessibility preferences</li>
                    <li>Anonymous user preferences</li>
                    <li>Platform customization settings</li>
                  </ul>
                </div>

                <div className="border-l-4 border-purple-400 pl-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Analytics Cookies (Optional)</h3>
                  <p className="text-gray-700 mb-2">These help us improve the platform:</p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-1">
                    <li>Anonymous usage statistics</li>
                    <li>Platform performance monitoring</li>
                    <li>Feature usage analysis (anonymized)</li>
                    <li>Error tracking and debugging</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Cookies We Do NOT Use</h2>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 font-medium mb-2">We do not use cookies for:</p>
                <ul className="list-disc pl-6 text-red-700 space-y-1">
                  <li>Advertising or marketing tracking</li>
                  <li>Social media tracking</li>
                  <li>Selling your data to third parties</li>
                  <li>Personal identification or profiling</li>
                  <li>Cross-site tracking</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Cookie Duration</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border-b">Cookie Type</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border-b">Duration</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 border-b">Purpose</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700">Session</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Until browser closes</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Maintain chat connections</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700">Preferences</td>
                      <td className="px-4 py-3 text-sm text-gray-700">30 days</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Remember settings</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700">Analytics</td>
                      <td className="px-4 py-3 text-sm text-gray-700">30 days</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Platform improvement</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Managing Your Cookie Preferences</h2>
              
              <h3 className="text-lg font-medium text-gray-900 mb-3">Platform Controls</h3>
              <p className="text-gray-700 mb-4">
                You can manage your cookie preferences directly on our platform through the cookie consent banner or in your account settings.
              </p>

              <h3 className="text-lg font-medium text-gray-900 mb-3">Browser Controls</h3>
              <p className="text-gray-700 mb-4">You can also control cookies through your browser settings:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies and other site data</li>
                <li><strong>Firefox:</strong> Preferences → Privacy & Security → Cookies and Site Data</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                <li><strong>Edge:</strong> Settings → Cookies and site permissions</li>
              </ul>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                <p className="text-amber-800">
                  <strong>Note:</strong> Disabling essential cookies may affect platform functionality and your ability to use our emotional support services.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Third-Party Cookies</h2>
              <p className="text-gray-700 mb-4">
                We minimize third-party cookies to protect your privacy. Any third-party services we use are carefully vetted and must comply with our privacy standards:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                <li>Content delivery networks (for platform performance)</li>
                <li>Security services (for platform protection)</li>
                <li>Anonymous analytics providers (for platform improvement)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Updates to This Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this cookie policy to reflect changes in our practices or for legal compliance. Significant changes will be communicated through our platform or via email.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have questions about our use of cookies or this policy, please contact us:
                <br />
                <a href="mailto:connect@vinematched.com" className="text-red-600 hover:text-red-700">connect@vinematched.com</a>
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}

export default CookiePolicy;